/* assets/ps-cart-guard.js — last-moment diamond availability check
 * ---------------------------------------------------------------------------
 * Pruden & Smith, 10 August 2026.
 *
 * A diamond can be taken between someone adding it to their bag and paying for
 * it. Where the supplier allows a hold we close that window; not all of them
 * do. This narrows it for the rest: the moment CHECK OUT is clicked, any
 * UNHELD diamond in the cart is re-checked live. A stone that has gone is
 * removed and explained BEFORE any money changes hands, rather than being
 * discovered after the customer has paid.
 *
 * ── ONLY UNHELD STONES ARE CHECKED, AND THAT IS THE WHOLE DESIGN ───────────
 * A stone we are holding reports as ON_HOLD, and /api/diamond/check filters to
 * AVAILABLE — so re-checking our own held stone answers "gone" and we would
 * delete the customer's own diamond at the payment button. diamond-selector.js
 * learned this the hard way (see its >20h rule). A held stone doesn't need
 * checking anyway: the hold is the guarantee. So: hold present -> skip;
 * no hold -> check. The two cases are exactly complementary.
 *
 * ── IT FAILS OPEN ──────────────────────────────────────────────────────────
 * Slow, errored, unreachable, unknown: the customer goes through to checkout.
 * Blocking a real purchase because our check had a bad moment costs far more
 * than the rare oversell it guards against — an oversell is recoverable by a
 * phone call, a lost sale is not. A stone KNOWN to be gone is the one case we
 * do not wave through.
 *
 * COVERAGE: complete, as the shop is set up today. The cart shows one CHECK OUT
 * button — the wallet logos beneath it are payment-method icons and a Klarna
 * information box, not buttons — and the product page offers Add to Cart only.
 * Apple Pay, Google Pay, Shop Pay and Klarna are all chosen INSIDE Shopify
 * checkout, i.e. after this check has run. The one thing that would open a gap
 * is switching on Shopify's dynamic checkout buttons ("Buy it now" / wallet
 * buttons on the product or cart page): those post straight to checkout from
 * their own markup and cannot be intercepted. Don't enable them on ring pages
 * without revisiting this.
 *
 * Add to layout/theme.liquid, near the diamond-selector.js tag:
 *   <script src="{{ 'ps-cart-guard.js' | asset_url }}" defer></script>
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.__psCartGuard) return;
  window.__psCartGuard = true;

  var API = 'https://nivoda-api.vercel.app';
  var BUDGET_MS = 7000;      // the longest we will make anyone wait
  var FRESH_MS = 60000;      // a pass this recent counts; don't re-check every click
  var GONE_KEY = 'psCartGuardGone';
  var lastPass = 0;

  function $all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* The theme's real checkout controls: <button type="submit" name="checkout">
     inside <form action="/cart" method="post">, on both the cart page and the
     drawer. NOT `.cart__checkout` — the Update button carries that class too. */
  function checkoutButtons() {
    return $all('button[name="checkout"], input[name="checkout"], [data-ps-checkout]');
  }

  function bundlesIn(cart) {
    var map = {};
    (cart.items || []).forEach(function (i) {
      var p = i.properties || {};
      if (!p._bundle_ref) return;
      var b = map[p._bundle_ref] || (map[p._bundle_ref] = { ref: p._bundle_ref, cert: '', hold: '', keys: [], title: '', url: '' });
      b.keys.push(i.key);
      if (p._diamond_cert) {
        b.cert = String(p._diamond_cert);
        b.hold = String(p._hold_id || '');
        b.title = i.title || '';
        b.url = i.url || '';
      }
    });
    return Object.keys(map).map(function (k) { return map[k]; }).filter(function (b) { return b.cert; });
  }

  function removeBundle(bundle) {
    var updates = {};
    bundle.keys.forEach(function (k) { updates[k] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { if (!r.ok) throw new Error('update ' + r.status); return r; });
  }

  function noticeHost(btn) {
    // In the drawer, the cart page's <main> is behind the overlay — the message
    // has to go where the customer is actually looking.
    var drawer = btn && btn.closest ? btn.closest('.drawer, [data-cart-drawer], #cart-drawer, aside') : null;
    if (drawer) return drawer.querySelector('.drawer__body, [data-cart-drawer-inner], form') || drawer;
    return document.querySelector('form[action*="/cart"]') || document.querySelector('.cart') || document.querySelector('main') || document.body;
  }

  function notice(msg, btn) {
    var host = noticeHost(btn);
    var box = document.getElementById('ps-cart-guard-notice');
    if (!box) {
      box = document.createElement('div');
      box.id = 'ps-cart-guard-notice';
      box.setAttribute('role', 'status');
      box.style.cssText = 'font-size:13px;line-height:1.55;padding:12px 14px;margin:0 0 14px;' +
        'border-left:3px solid #9a7b4f;background:#faf8f4;color:#3a352f;';
    }
    box.textContent = msg;
    if (box.parentNode !== host) host.insertBefore(box, host.firstChild);
    try { box.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
  }

  // Survives the reload that follows, so the explanation isn't lost with the page.
  function rememberGone(msg) {
    try { sessionStorage.setItem(GONE_KEY, msg); } catch (e) {}
  }
  function showRememberedGone() {
    var msg;
    try { msg = sessionStorage.getItem(GONE_KEY); sessionStorage.removeItem(GONE_KEY); } catch (e) { return; }
    if (msg) notice(msg, null);
  }

  function busy(btn, on, original) {
    if (!btn) return;
    if (on) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('data-ps-original', original == null ? '' : original);
      btn.textContent = 'Checking your diamond…';
      btn.disabled = true;
    } else {
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
      var t = btn.getAttribute('data-ps-original');
      if (t) btn.textContent = t;
      btn.removeAttribute('data-ps-original');
    }
  }

  /* Hand the customer's own click back to the browser.
     form.submit() is NOT good enough: it submits without a submitter, so
     name="checkout" never reaches Shopify and the post reads as a quantity
     update — the customer would be bounced straight back to the cart. And a
     DISABLED button is excluded from the submission entirely, so the button
     must be re-enabled first. Both of those turn this file into a shop that
     cannot take money. */
  function proceed(btn) {
    lastPass = Date.now();
    busy(btn, false);
    var form = btn && btn.form;
    if (form) {
      if (typeof form.requestSubmit === 'function') { form.requestSubmit(btn); return; }
      var hidden = document.createElement('input'); // Safari < 16
      hidden.type = 'hidden'; hidden.name = btn.getAttribute('name') || 'checkout'; hidden.value = btn.value || '';
      form.appendChild(hidden);
      form.submit();
      return;
    }
    if (btn && btn.tagName === 'A' && btn.getAttribute('href')) { window.location.href = btn.getAttribute('href'); return; }
    window.location.href = '/checkout';
  }

  function onCheckout(e) {
    var btn = e.currentTarget;
    if (btn.__psBusy) { e.preventDefault(); return; }        // ignore double-clicks
    if (Date.now() - lastPass < FRESH_MS) return;            // just checked — let it go
    e.preventDefault();

    btn.__psBusy = true;
    busy(btn, true, btn.textContent);

    var ctl = (typeof AbortController === 'function') ? new AbortController() : null;
    var done = false;
    function finish(fn) {
      if (done) return;
      done = true;
      clearTimeout(timer);
      btn.__psBusy = false;
      fn();
    }
    // A deadline that ACTS. A flag checked later never fires if the promise
    // chain simply never settles — which is what a stalled fetch does.
    var timer = setTimeout(function () {
      if (ctl) ctl.abort();
      finish(function () { proceed(btn); });
    }, BUDGET_MS);

    var opts = ctl ? { signal: ctl.signal } : {};

    fetch('/cart.js', { headers: { Accept: 'application/json' }, signal: opts.signal })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Held stones are already safe — and re-checking one reports our own
        // hold as "gone". Only the unheld ones need asking about.
        var atRisk = bundlesIn(cart).filter(function (b) { return !b.hold; });
        if (!atRisk.length) return [];
        return Promise.all(atRisk.map(function (b) {
          return fetch(API + '/api/diamond/check', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ certNumber: b.cert }),
            signal: opts.signal
          })
            .then(function (r) { if (!r.ok) throw new Error('check ' + r.status); return r.json(); })
            .then(function (j) { return (j && j.available === false) ? b : null; })
            .catch(function () { return null; });          // unknown is NOT gone
        })).then(function (rows) { return rows.filter(Boolean); });
      })
      .then(function (gone) {
        if (!gone.length) { finish(function () { proceed(btn); }); return; }

        var msg = gone.length === 1
          ? 'One of the diamonds in your bag was taken by another customer a few minutes ago, so we’ve removed it. Please choose another — everything showing is available right now. Sorry about that.'
          : 'Some of the diamonds in your bag have just been taken, so we’ve removed them. Please choose again — everything showing is available right now. Sorry about that.';

        // A stone we KNOW has gone is the one thing we never wave through — so
        // this path must not fall into the fail-open catch below.
        return Promise.all(gone.map(removeBundle))
          .catch(function () { /* removal failed — still refuse to sell it */ })
          .then(function () {
            finish(function () {
              notice(msg, btn);
              rememberGone(msg);
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
              setTimeout(function () { window.location.reload(); }, 2400);
            });
          });
      })
      .catch(function () {
        finish(function () { proceed(btn); });               // any failure: let them buy
      });
  }

  var bindTimer = null;
  function bind() {
    checkoutButtons().forEach(function (btn) {
      if (btn.__psGuarded) return;
      btn.__psGuarded = true;
      btn.addEventListener('click', onCheckout);
    });
  }
  function bindSoon() {
    if (bindTimer) return;
    bindTimer = setTimeout(function () { bindTimer = null; bind(); }, 120);
  }

  bind();
  showRememberedGone();
  // Cart drawers build their button after load and rebuild it on every change.
  if (window.MutationObserver) {
    try { new MutationObserver(bindSoon).observe(document.body, { childList: true, subtree: true }); } catch (e) {}
  }
  document.addEventListener('cart:updated', bindSoon);
  document.addEventListener('cart:refresh', bindSoon);
})();
